import { useEffect, useMemo, useState } from "react";
import { Building2, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import { getPlans } from "../../api/plans";
import { useAuth } from "../../app/providers/AuthProvider";
import PageHeader from "../../components/common/PageHeader";
import {
  PASSWORD_POLICY_MESSAGE,
  validatePasswordPolicy,
} from "../../utils/passwordPolicy";

const CREATE_BUSINESS_ENDPOINT = "/platform-onboarding/businesses";
const PARTNER_LOOKUP_ENDPOINT = "/platform-onboarding/partners/lookup";
const RESELLER_LOOKUP_ENDPOINT = "/platform-onboarding/resellers/lookup";

function getErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.errorMessage ||
    error?.message ||
    fallbackMessage
  );
}

function extractList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export default function CreateBusinessPage() {
  const navigate = useNavigate();
  const { role, roleKey } = useAuth();
  const [form, setForm] = useState({
    companyName: "",
    fullName: "",
    email: "",
    password: "",
    businessEmail: "",
    phone: "",
    website: "",
    country: "",
    state: "",
    industry: "",
    ownershipScope: "direct",
    partnerUserId: "",
    resellerUserId: "",
    planId: "",
    businessStatus: "Approved",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [resellerOptions, setResellerOptions] = useState([]);
  const [planOptions, setPlanOptions] = useState([]);
  const [isLoadingLookups, setIsLoadingLookups] = useState(false);

  const normalizedRoleKey = useMemo(() => {
    const rawRole = String(role || "").trim().toLowerCase();
    const rawRoleKey = String(roleKey || "").trim().toLowerCase();

    if (rawRoleKey) return rawRoleKey;
    if (rawRole === "admin" || rawRole === "superadmin") {
      return "platform.super_admin";
    }
    if (rawRole === "partner") return "platform.partner";
    if (rawRole === "reseller") return "platform.reseller";
    return "";
  }, [role, roleKey]);

  const isPlatformAdmin = normalizedRoleKey === "platform.super_admin";
  const isPartnerActor = normalizedRoleKey === "platform.partner";
  const isResellerActor = normalizedRoleKey === "platform.reseller";

  const redirectPath =
    isPartnerActor || isResellerActor
      ? "/app/welcomepage"
      : "/app/admin/approvals";

  useEffect(() => {
    let isMounted = true;

    if (!isPlatformAdmin) {
      setPartnerOptions([]);
      setResellerOptions([]);
      setPlanOptions([]);
      setIsLoadingLookups(false);
      return () => {
        isMounted = false;
      };
    }

    const loadLookups = async () => {
      try {
        setIsLoadingLookups(true);

        const [partnersResponse, resellersResponse, plansResponse] =
          await Promise.all([
            axiosClient.get(PARTNER_LOOKUP_ENDPOINT, { __skipBizHeader: true }),
            axiosClient.get(RESELLER_LOOKUP_ENDPOINT, { __skipBizHeader: true }),
            getPlans(),
          ]);

        if (!isMounted) return;

        const plans = extractList(plansResponse.data);
        const defaultPlanId =
          plans.find(
            plan => String(plan?.code || "").trim().toUpperCase() === "BASIC"
          )?.id ||
          plans[0]?.id ||
          "";

        setPartnerOptions(extractList(partnersResponse.data));
        setResellerOptions(extractList(resellersResponse.data));
        setPlanOptions(plans);
        setForm(current => ({
          ...current,
          planId: current.planId || defaultPlanId,
        }));
      } catch (error) {
        if (!isMounted) return;
        const message = getErrorMessage(
          error,
          "Failed to load business onboarding options."
        );
        setSubmitError(message);
        toast.error(message);
      } finally {
        if (isMounted) {
          setIsLoadingLookups(false);
        }
      }
    };

    loadLookups();

    return () => {
      isMounted = false;
    };
  }, [isPlatformAdmin]);

  const handleChange = event => {
    const { name, value } = event.target;
    setForm(current => {
      if (name === "ownershipScope") {
        return {
          ...current,
          ownershipScope: value,
          partnerUserId: value === "partner" ? current.partnerUserId : "",
          resellerUserId: value === "reseller" ? current.resellerUserId : "",
        };
      }

      return { ...current, [name]: value };
    });
    setFieldErrors(current => ({ ...current, [name]: "" }));
    setSubmitError("");
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.companyName.trim()) {
      nextErrors.companyName = "Company name is required.";
    }

    if (!form.fullName.trim()) {
      nextErrors.fullName = "Owner name is required.";
    }

    if (!form.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!isValidEmail(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }

    if (form.businessEmail.trim() && !isValidEmail(form.businessEmail)) {
      nextErrors.businessEmail = "Enter a valid business contact email.";
    }

    const passwordError = validatePasswordPolicy(form.password);
    if (passwordError) {
      nextErrors.password = passwordError;
    }

    if (isPlatformAdmin && form.ownershipScope === "partner" && !form.partnerUserId) {
      nextErrors.partnerUserId = "Select the owning partner.";
    }

    if (isPlatformAdmin && form.ownershipScope === "reseller" && !form.resellerUserId) {
      nextErrors.resellerUserId = "Select the owning reseller.";
    }

    if (isPlatformAdmin && !form.planId) {
      nextErrors.planId = "Select a plan.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      setSubmitError("");

      await axiosClient.post(
        CREATE_BUSINESS_ENDPOINT,
        {
          companyName: form.companyName.trim(),
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          password: form.password,
          businessEmail: form.businessEmail.trim() || null,
          phone: form.phone.trim() || null,
          website: form.website.trim() || null,
          country: form.country.trim() || null,
          state: form.state.trim() || null,
          industry: form.industry.trim() || null,
          partnerUserId:
            isPlatformAdmin && form.ownershipScope === "partner"
              ? form.partnerUserId
              : null,
          resellerUserId:
            isPlatformAdmin && form.ownershipScope === "reseller"
              ? form.resellerUserId
              : null,
          planId: isPlatformAdmin ? form.planId || null : null,
          businessStatus: isPlatformAdmin ? form.businessStatus : null,
        },
        {
          __skipBizHeader: true,
        }
      );

      toast.success("Business created successfully");
      navigate(redirectPath, { replace: true });
    } catch (error) {
      const message = getErrorMessage(error, "Failed to create business.");
      setSubmitError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (name, label, type = "text", autoComplete) => (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <input
        type={type}
        name={name}
        value={form[name]}
        onChange={handleChange}
        autoComplete={autoComplete}
        className={[
          "w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition",
          fieldErrors[name]
            ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100",
        ].join(" ")}
      />
      {fieldErrors[name] ? (
        <div className="text-xs font-medium text-rose-600">{fieldErrors[name]}</div>
      ) : null}
    </label>
  );

  const renderSelect = (
    name,
    label,
    options,
    { placeholder = null, disabled = false } = {}
  ) => (
    <label className="space-y-1.5">
      <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
        {label}
      </span>
      <select
        name={name}
        value={form[name]}
        onChange={handleChange}
        disabled={disabled}
        className={[
          "w-full rounded-[10px] border bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition",
          fieldErrors[name]
            ? "border-rose-300 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
            : "border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100",
          disabled ? "cursor-not-allowed bg-slate-50" : "",
        ].join(" ")}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {fieldErrors[name] ? (
        <div className="text-xs font-medium text-rose-600">{fieldErrors[name]}</div>
      ) : null}
    </label>
  );

  const ownershipOptions = [
    { value: "direct", label: "Direct Platform" },
    { value: "partner", label: "Partner" },
    { value: "reseller", label: "Reseller" },
  ];

  const partnerSelectOptions = partnerOptions.map(option => ({
    value: option.userId,
    label: `${option.fullName || option.email} (${option.email})`,
  }));

  const resellerSelectOptions = resellerOptions.map(option => {
    const partnerLabel = option.partnerEmail
      ? `Partner: ${option.partnerName || option.partnerEmail}`
      : "Direct reseller";

    return {
      value: option.userId,
      label: `${option.fullName || option.email} (${option.email}) | ${partnerLabel}`,
    };
  });

  const planSelectOptions = planOptions.map(option => ({
    value: option.id,
    label: option.name || option.code || option.id,
  }));

  return (
    <div className="min-h-screen bg-[#f3f4f6] py-6">
      <section className="px-4">
        <div className="mx-auto max-w-[960px] space-y-6">
          <div className="px-4 pt-4 pb-1">
            <PageHeader
              icon={Building2}
              title="Create Business"
              subtitle="Provision a business with explicit hierarchy, plan, and onboarding details"
            />
          </div>

          <div className="px-4">
            <div className="rounded-[10px] border border-slate-200 bg-white shadow-sm">
              <form onSubmit={handleSubmit} className="space-y-6 px-5 py-5">
                {submitError ? (
                  <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                    {submitError}
                  </div>
                ) : null}

                <div className="space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Owner Account</div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {renderField("companyName", "Company Name", "text", "organization")}
                    {renderField("fullName", "Owner Name", "text", "name")}
                    {renderField("email", "Owner Email", "email", "email")}
                    {renderField("password", "Owner Password", "password", "new-password")}
                  </div>
                  <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    {PASSWORD_POLICY_MESSAGE}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Business Details</div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {renderField("businessEmail", "Business Contact Email", "email", "email")}
                    {renderField("phone", "Business Phone", "tel", "tel")}
                    {renderField("website", "Website", "url", "url")}
                    {renderField("industry", "Industry", "text", "organization-title")}
                    {renderField("country", "Country", "text", "country-name")}
                    {renderField("state", "State", "text", "address-level1")}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-semibold text-slate-700">Ownership</div>

                  {isPlatformAdmin ? (
                    <div className="grid gap-4 md:grid-cols-2">
                      {renderSelect("ownershipScope", "Ownership Scope", ownershipOptions)}
                      {form.ownershipScope === "partner"
                        ? renderSelect("partnerUserId", "Owning Partner", partnerSelectOptions, {
                            placeholder: isLoadingLookups
                              ? "Loading partners..."
                              : "Select partner",
                            disabled: isLoadingLookups,
                          })
                        : null}
                      {form.ownershipScope === "reseller"
                        ? renderSelect("resellerUserId", "Owning Reseller", resellerSelectOptions, {
                            placeholder: isLoadingLookups
                              ? "Loading resellers..."
                              : "Select reseller",
                            disabled: isLoadingLookups,
                          })
                        : null}
                    </div>
                  ) : (
                    <div className="rounded-[10px] border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                      {isPartnerActor
                        ? "This business will be assigned to your current partner account."
                        : "This business will be assigned to your current reseller account. If that reseller has a linked partner, the business will inherit it automatically."}
                    </div>
                  )}
                </div>

                {isPlatformAdmin ? (
                  <div className="space-y-3">
                    <div className="text-sm font-semibold text-slate-700">
                      Provisioning Controls
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {renderSelect("planId", "Plan", planSelectOptions, {
                        placeholder: isLoadingLookups ? "Loading plans..." : "Select plan",
                        disabled: isLoadingLookups,
                      })}
                      {renderSelect("businessStatus", "Business Status", [
                        { value: "Approved", label: "Approved" },
                        { value: "Hold", label: "Hold" },
                      ])}
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isSubmitting || isLoadingLookups}
                    className="inline-flex items-center gap-2 rounded-[10px] bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Business"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
